import { PlusIcon } from '@heroicons/react/24/solid'
import { isEqual, isNumber } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotificationContext, usePartnerContext } from '../../../context'
import { mutation } from '../../../graphql'
import { useSetState } from '../../../hooks/use-set-state'
import OldButton from '../../button'
import CulturalFitQuestions from '../../cultural-fit/cultural-fit-questions'
import HerosJourneyMatrix from '../../cultural-fit/heros-journey-matrix'
import Button from '../../tailwind/Button'
import Modal from '../../tailwind/Modal'
import SettingsContainer from '../settings-container'

const CulturalFitCompany = () => {
  const { t } = useTranslation()
  const { partner, refetchPartner } = usePartnerContext()
  const { success } = useNotificationContext()

  const cultural_fit_partner = JSON.parse(
    partner.app_settings?.cultural_fit || '{}'
  )

  const [openEditCulturalFitProfile, setOpenEditCulturalFitProfile] =
    useState(false)

  const [cultureFitState, setCultureFitState] =
    useSetState(cultural_fit_partner)

  const [isSubmitting, setSubmitting] = useState(false)

  const partner_has_culture_profile =
    isNumber(cultural_fit_partner.HEROS_JOURNEY_score_coopcomp) &&
    isNumber(cultural_fit_partner.HEROS_JOURNEY_score_orderchaos)

  const title = t('settings.company.cultural_fit.title')
  const description = t('settings.company.cultural_fit.description')

  const handleClose = () => {
    setOpenEditCulturalFitProfile(false)
    // await transition
    setTimeout(() => {
      if (!partner_has_culture_profile) {
        setCultureFitState({
          HEROS_JOURNEY_score_coopcomp: undefined,
          HEROS_JOURNEY_score_orderchaos: undefined
        })
      } else setCultureFitState(cultural_fit_partner)
    }, 0.4 * 1000)
  }

  const handleSubmit = async () => {
    setSubmitting(true)

    await mutation({
      mutation: 'updatePartner',
      input: {
        id: partner.id,
        app_settings: {
          ...(partner.app_settings || {}),
          cultural_fit: JSON.stringify({ ...cultureFitState })
        }
      }
    })

    await refetchPartner()

    setSubmitting(false)
    setOpenEditCulturalFitProfile(false)
    success(t('settings.company.cultural_fit.success_message'))
  }

  return (
    <>
      <SettingsContainer
        title={title}
        description={description}
        actions={
          partner_has_culture_profile ? (
            <OldButton
              appearance='edit'
              text={t('settings.company.cultural_fit.edit_action')}
              onClick={() => setOpenEditCulturalFitProfile(true)}
            />
          ) : null
        }
      >
        <div className='flex justify-center'>
          {partner_has_culture_profile ? (
            <HerosJourneyMatrix
              className='mx-4 my-8 h-40 w-56 sm:h-80 sm:w-96'
              culturalFitValues={cultural_fit_partner}
            />
          ) : (
            <button
              type='button'
              onClick={() => setOpenEditCulturalFitProfile(true)}
              className='group relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-0 focus:ring-offset-0'
            >
              <PlusIcon className='mx-auto size-12 text-gray-400 group-hover:text-gray-500' />
              <span className='mt-3 block text-sm font-medium text-gray-700 group-hover:text-gray-900'>
                {t('settings.company.cultural_fit.empty_action')}
              </span>
            </button>
          )}
        </div>
      </SettingsContainer>
      <Modal open={openEditCulturalFitProfile} setOpen={handleClose} size='4xl'>
        <div className='p-2'>
          <h1 className='text-xl font-semibold text-gray-900'>{title}</h1>
          <p className='mt-1 max-w-lg text-sm text-gray-700 lg:max-w-2xl'>
            {description}
          </p>
          <div className='mt-4' />
          <CulturalFitQuestions
            state={cultureFitState}
            setState={setCultureFitState}
          />
          <div className='mt-8 flex justify-end gap-x-2'>
            <Button.SecondaryLG
              onClick={handleClose}
              text={t('settings.company.cultural_fit.edit_cancel_action')}
            />
            <Button.PrimaryLG
              text={t('settings.company.cultural_fit.edit_save_action')}
              isLoading={isSubmitting}
              disabled={isEqual(cultureFitState, cultural_fit_partner)}
              onClick={handleSubmit}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}

export default CulturalFitCompany
